import { Navigate, Route, Routes as ReactRouterRoutes } from 'react-router-dom'
import { PublicRouteGuard, AuthenticatedRouteGuard } from './Guards'
import { useAuth } from 'components/AuthProvider'
import buildLink from 'utils/buildLink'

// Import pages
import Activity from 'components/pages/Activity'
import BankAccounts from 'components/pages/BankAccount'
import Buy from 'components/pages/Buy'
import BuyCelebration from 'components/pages/BuyCelebration'
import ChangePassword from 'components/pages/ChangePassword'
import ComingSoon from 'components/pages/ComingSoon'
import ConfirmEmail from 'components/pages/ConfirmEmail'
import Contact from 'components/pages/Contact/Contact'
import CreateReviewOrder from 'components/pages/CreateReviewOrder'
import DepositFunds from 'components/pages/DepositFunds'
import DeviceSettings from 'components/pages/DeviceSettings'
import ForgotPassword from 'components/pages/ForgotPassword'
import Gifts from 'components/pages/Gifts'
import KidVault from 'components/pages/KidVault'
import CreateKidVault from 'components/pages/KidVault/Create'
import UpdateKidVault from 'components/pages/KidVault/Update'
import Login from 'components/pages/Login'
import More from 'components/pages/More'
import MyVault from 'components/pages/MyVault'
import TradeProduct from 'components/pages/TradeProduct'
import OnboardingWizard from 'components/pages/OnboardingWizard'
import VerificationError from 'components/pages/OnboardingWizard/VerificationError'
import VerificationPartialSuccess from 'components/pages/OnboardingWizard/VerificationPartialSuccess'
import VerificationSuccess from 'components/pages/OnboardingWizard/VerificationSuccess'
import Verifying from 'components/pages/OnboardingWizard/Verifying'
import BlinkPayTransferLoading from 'components/pages/BlinkPayTransferLoading'
import BlinkPayTransferStatus from 'components/pages/BlinkPayTransferStatus'
import AppReturn from 'components/pages/AppReturn'
import POLiTransferLoading from 'components/pages/POLiTransferLoading'
import POLiTransferStatus from 'components/pages/POLiTransferStatus'
import PersonalDetails from 'components/pages/PersonalDetails'
import ProfileSettings from 'components/pages/ProfileSettings'
import Recharge from 'components/pages/Recharge'
import RechargeConfirm from 'components/pages/RechargeConfirm/RechargeConfirm'
import RedeemVoucher from 'components/pages/RedeemVoucher'
import ReferralCode from 'components/pages/ReferralCode'
import ResendConfirmationEmail from 'components/pages/ResendConfirmationEmail'
import ResetPassword from 'components/pages/ResetPassword'
import Review from 'components/pages/Review/Review'
import SignUp from 'components/pages/SignUp'
import Tutorial from 'components/pages/Tutorial'
import TwoStepAuthentication from 'components/pages/TwoStepAuthentication'
import Reenrol from 'components/pages/TwoStepAuthentication/Reenrol'
import VerifyOtp from 'components/pages/VerifyOtp'
import Wallet from 'components/pages/Wallet'
import Welcome from 'components/pages/Welcome'
import WithdrawFunds from 'components/pages/WithdrawFunds'
import AddBankAccount from 'components/pages/AddBankAccount'
import VoucherFlow from './VoucherFlowRoutes/VoucherFlowRoutes'
import { PathArgs } from './types'
import AutoInvest from 'components/pages/AutoInvest'
import AddAutoInvest from 'components/pages/AddAutoInvest'
import InstallApp from 'components/pages/InstallApp'
import VerificationOther from 'components/pages/OnboardingWizard/VerificationOther'
import AutoInvestSuccess from 'components/pages/AutoInvestSuccess'

/**
 * TODO: consider "verified" routes - like "authenticated", but hiding screens that require
 * the user to have completed AML and ID verification
 */

function buyPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/buy/:reviewOrderId' })
}
function buyCelebrationPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/buyCelebration/:productId' })
}
function confirmEmailPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/confirm' })
}
function createReviewOrderPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/new' })
}
function dashboardPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/dashboard' })
}

function forgotPasswordPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/forgot_password' })
}

function loginPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/login' })
}

function resendConfirmationEmailPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/resend_confirmation_email' })
}
function resetPasswordPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/reset_password' })
}
function reviewPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/review/:reviewOrderId' })
}
function sellPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/sell/:reviewOrderId' })
}
function signUpPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/sign_up' })
}
function verificationPartialSuccessPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/verification_partial_success' })
}
function verificationErrorPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/verification_error' })
}
function verificationSuccessPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/verification_success' })
}
function verificationOtherPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/verification_other' })
}
function verifyFinancialDetailsPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/verify/financial_details' })
}
function verifyIdDetailsPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/verify/id_details' })
}
function verifyPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/verify' })
}
function verifyGetStartedPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/verify/get_started' })
}
function verifyPersonalDetailsPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/verify/personal_details' })
}
function verifyingPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/verifying' })
}
function verifyOtpPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/verify_otp' })
}

function welcomePath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/welcome' })
}
function comingSoonPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/coming_soon' })
}

function tutorialPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/tutorial' })
}

function homePath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/home' })
}
function contactPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/contact' })
}

/* TRADE ROUTES */
function tradePath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/trade/:productId' })
}

function myVaultPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/vault' })
}
function myVaultProductPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/vault/:productId' })
}

function settingsPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/profile_settings' })
}
function personalDetailsPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/personal_details' })
}
function bankAccountPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/bank_account' })
}
function addBankAccountFormPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/bank_account/add' })
}
function updateBankAccountPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/update_bank_account' })
}

function twoStepAuthenticationPath(args?: PathArgs): string {
  return buildLink({
    ...args,
    path: '/account/more/two_step_authentication'
  })
}
function twoStepDownloadPath(args?: PathArgs): string {
  return buildLink({
    ...args,
    path: '/account/more/two_step_authentication/download'
  })
}
function twoStepScanPath(args?: PathArgs): string {
  return buildLink({
    ...args,
    path: '/account/more/two_step_authentication/scan'
  })
}
function twoStepCodePath(args?: PathArgs): string {
  return buildLink({
    ...args,
    path: '/account/more/two_step_authentication/code'
  })
}
function deviceSettingsPath(args?: PathArgs): string {
  return buildLink({
    ...args,
    path: '/account/more/device_settings'
  })
}
function kidVaultPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/kid_vault' })
}
function createKidVaultPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/kid_vault/create' })
}
function updateKidVaultPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/kid_vault/update/:id' })
}
function walletPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/wallet' })
}
function depositFundsPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/deposit_funds' })
}
function rechargeConfirmPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/recharge_confirm' })
}
function rechargePath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/recharge' })
}
function withdrawFundsPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/withdraw' })
}
function activityPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/activity' })
}
function changePasswordPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/password' })
}
function referralsPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more/referrals' })
}
function morePath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/more' })
}
function voucherFlowPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/gifts/voucher' })
}
function redeemVoucherPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/gifts/redeem' })
}
function giftsPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/account/gifts' })
}

// POLi transfer routes
function POLiTransferLoadingPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/wallet/POLi_transfer/loading' })
}
function POLiTransferStatusPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/wallet/POLi_transfer/status' })
}

// BlinkPay transfer routes (must match backend redirect_uri: /wallet/BlinkPay/loading)
function BlinkPayTransferLoadingPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/wallet/BlinkPay/loading' })
}
function BlinkPayTransferStatusPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/wallet/BlinkPay/status' })
}
// The MOBILE app's BlinkPay redirect_uri (goldie-app wallet/data.ts) — hands back to the app.
function appReturnPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/wallet/deposit/complete' })
}

// Auto invest
function autoInvestPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/auto_invest' })
}
function addAutoInvestPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/auto_invest/add' })
}
function autoInvestSuccessPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/auto_invest/success' })
}

function installAppPath(args?: PathArgs): string {
  return buildLink({ ...args, path: '/install_app' })
}

const BlinkPayReturn = (): JSX.Element => {
  const { user } = useAuth()
  return user ? <BlinkPayTransferLoading /> : <AppReturn />
}

const Routes = (): JSX.Element => {
  return (
    <ReactRouterRoutes>
      {/* No guard: the mobile app's in-app browser lands here signed out. */}
      <Route path={appReturnPath()} element={<AppReturn />} />
      {/* BlinkPay's live whitelist holds ONE https return URL, shared by this dashboard and
          the mobile app: signed in → the dashboard's verify/loading page; nobody signed in
          (the app's in-app browser) → hand straight back to the app. */}
      <Route
        path={BlinkPayTransferLoadingPath()}
        element={<BlinkPayReturn />}
      />
      {/* Public routes - accessible only when NOT logged in */}
      <Route element={<PublicRouteGuard />}>
        <Route path={loginPath()} element={<Login />} />
        <Route path={signUpPath()} element={<SignUp />} />
        <Route path={verifyOtpPath()} element={<VerifyOtp />} />
        <Route path={confirmEmailPath()} element={<ConfirmEmail />} />
        <Route path={forgotPasswordPath()} element={<ForgotPassword />} />
        <Route
          path={resendConfirmationEmailPath()}
          element={<ResendConfirmationEmail />}
        />
        <Route path={resetPasswordPath()} element={<ResetPassword />} />
      </Route>

      {/*Authenticated routes - accessible only when logged in */}
      <Route element={<AuthenticatedRouteGuard />}>
        <Route path={welcomePath()} element={<Welcome />} />
        <Route path={activityPath()} element={<Activity />} />
        <Route path={createReviewOrderPath()} element={<CreateReviewOrder />} />
        <Route path={buyPath()} element={<Buy />} />
        <Route path={reviewPath()} element={<Review />} />
        <Route path={settingsPath()} element={<ProfileSettings />} />
        <Route path={buyCelebrationPath()} element={<BuyCelebration />} />
        <Route path={depositFundsPath()} element={<DepositFunds />} />
        <Route
          path={POLiTransferLoadingPath()}
          element={<POLiTransferLoading />}
        />
        <Route
          path={POLiTransferStatusPath()}
          element={<POLiTransferStatus />}
        />
        <Route
          path={BlinkPayTransferStatusPath()}
          element={<BlinkPayTransferStatus />}
        />
        <Route path={rechargeConfirmPath()} element={<RechargeConfirm />} />
        <Route path={rechargePath()} element={<Recharge />} />
        <Route path={withdrawFundsPath()} element={<WithdrawFunds />} />
        <Route path={updateBankAccountPath()} element={<BankAccounts />} />
        <Route path={bankAccountPath()} element={<BankAccounts />} />
        <Route path={addBankAccountFormPath()} element={<AddBankAccount />} />
        <Route path={personalDetailsPath()} element={<PersonalDetails />} />
        <Route path={changePasswordPath()} element={<ChangePassword />} />
        <Route
          path='/account/settings/two_step_authentication/re-enrol'
          element={<Reenrol />}
        />
        <Route
          path={twoStepAuthenticationPath() + '/*'}
          element={<TwoStepAuthentication />}
        />
        <Route path={deviceSettingsPath()} element={<DeviceSettings />} />
        <Route path={redeemVoucherPath()} element={<RedeemVoucher />} />
        <Route path={walletPath()} element={<Wallet />} />
        <Route path={verifyPath() + '/*'} element={<OnboardingWizard />} />
        <Route path={verifyingPath()} element={<Verifying />} />
        <Route
          path={verificationSuccessPath()}
          element={<VerificationSuccess />}
        />
        <Route path={verificationErrorPath()} element={<VerificationError />} />
        <Route
          path={verificationPartialSuccessPath()}
          element={<VerificationPartialSuccess />}
        />
        <Route path={verificationOtherPath()} element={<VerificationOther />} />
        <Route path={comingSoonPath()} element={<ComingSoon />} />

        <Route path={tutorialPath()} element={<Tutorial />} />
        <Route path={referralsPath()} element={<ReferralCode />} />
        <Route path={contactPath()} element={<Contact />} />

        {/* TRADE ROUTES */}
        <Route path={tradePath()} element={<TradeProduct />} />
        <Route path={dashboardPath()} element={<TradeProduct />} />

        {/* VAULT ROUTES */}
        <Route path={createKidVaultPath()} element={<CreateKidVault />} />
        <Route path={updateKidVaultPath()} element={<UpdateKidVault />} />
        <Route path={kidVaultPath()} element={<KidVault />} />
        <Route path={myVaultPath()} element={<MyVault />} />
        {/* END VAULT ROUTES */}

        {/* ACCOUNT ROUTES */}
        <Route path={morePath()} element={<More />} />
        <Route path={voucherFlowPath() + '/*'} element={<VoucherFlow />} />
        <Route path={giftsPath()} element={<Gifts />} />
        <Route path={autoInvestSuccessPath()} element={<AutoInvestSuccess />} />
        <Route path={addAutoInvestPath()} element={<AddAutoInvest />} />
        <Route path={autoInvestPath()} element={<AutoInvest />} />
        <Route path={installAppPath()} element={<InstallApp />} />
        {/* END ACCOUNT ROUTES */}
      </Route>
      <Route path='*' element={<Navigate to={dashboardPath()} />} />
    </ReactRouterRoutes>
  )
}

export default Routes
export {
  BlinkPayTransferLoadingPath,
  BlinkPayTransferStatusPath,
  POLiTransferLoadingPath,
  POLiTransferStatusPath,
  activityPath,
  addBankAccountFormPath,
  bankAccountPath,
  buyCelebrationPath,
  buyPath,
  changePasswordPath,
  comingSoonPath,
  confirmEmailPath,
  contactPath,
  createKidVaultPath,
  createReviewOrderPath,
  dashboardPath,
  depositFundsPath,
  deviceSettingsPath,
  forgotPasswordPath,
  giftsPath,
  homePath,
  kidVaultPath,
  loginPath,
  morePath,
  myVaultPath,
  myVaultProductPath,
  personalDetailsPath,
  rechargeConfirmPath,
  rechargePath,
  redeemVoucherPath,
  referralsPath,
  resendConfirmationEmailPath,
  resetPasswordPath,
  reviewPath,
  sellPath,
  settingsPath,
  signUpPath,
  tradePath,
  tutorialPath,
  twoStepAuthenticationPath,
  twoStepCodePath,
  twoStepDownloadPath,
  twoStepScanPath,
  updateKidVaultPath,
  verificationErrorPath,
  verificationPartialSuccessPath,
  verificationSuccessPath,
  verifyFinancialDetailsPath,
  verifyGetStartedPath,
  verifyIdDetailsPath,
  verifyOtpPath,
  verificationOtherPath,
  verifyPath,
  verifyPersonalDetailsPath,
  verifyingPath,
  voucherFlowPath,
  walletPath,
  welcomePath,
  withdrawFundsPath,
  updateBankAccountPath,
  autoInvestPath,
  addAutoInvestPath,
  autoInvestSuccessPath,
  installAppPath
}
